// Canonical date-formatting utility for the workspace.
//
// This module is intentionally self-contained (only depends on `Intl`) so it can
// live in the lowest-level package (`@scott/ui`) and be exposed through the
// canonical `@scott/ui/utils/format-date` entrypoint. The portal and
// `@scott/ui-b2b` retain their own convenience entrypoints.
export type DateFormatConfig = {
  dateFormat: "DD.MM.YYYY" | "DD/MM/YYYY" | "MM/DD/YYYY";
  hour12: boolean;
};

export const countryFormatMap: Record<string, DateFormatConfig> = {
  AT: { dateFormat: "DD.MM.YYYY", hour12: false },
  BE: { dateFormat: "DD.MM.YYYY", hour12: false },
  DK: { dateFormat: "DD.MM.YYYY", hour12: false },
  FR: { dateFormat: "DD.MM.YYYY", hour12: false },
  DE: { dateFormat: "DD.MM.YYYY", hour12: false },
  IT: { dateFormat: "DD/MM/YYYY", hour12: false },
  NL: { dateFormat: "DD.MM.YYYY", hour12: false },
  CH: { dateFormat: "DD.MM.YYYY", hour12: false },
  SE: { dateFormat: "DD.MM.YYYY", hour12: false },
  GB: { dateFormat: "DD/MM/YYYY", hour12: true },

  // Future countries (already supported)
  AU: { dateFormat: "DD/MM/YYYY", hour12: true },
  NZ: { dateFormat: "DD/MM/YYYY", hour12: true },
  US: { dateFormat: "MM/DD/YYYY", hour12: true },
};

const DEFAULT_DATE_FORMAT: DateFormatConfig["dateFormat"] = "DD.MM.YYYY";

function applyDateFormat(
  format: DateFormatConfig["dateFormat"],
  day: string,
  month: string,
  year: string
): string {
  switch (format) {
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    default:
      return `${day}.${month}.${year}`;
  }
}

/**
 * Formats an ISO date-only string (YYYY-MM-DD) using the day/month/year order
 * standardized for the user's business country. Parsing is done on the string
 * parts so the rendered day never shifts across timezones. Returns an em dash
 * for malformed input.
 */
export function formatOrderDate(dateOnly: string, country?: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return "—";
  }

  const [year, month, day] = dateOnly.split("-");
  const format =
    (country ? countryFormatMap[country]?.dateFormat : undefined) ??
    DEFAULT_DATE_FORMAT;

  return applyDateFormat(format, day, month, year);
}

export function formatDateTime(
  date: Date,
  countryCode: string
): { date: string; time: string } {
  const config = countryFormatMap[countryCode] ?? {
    dateFormat: DEFAULT_DATE_FORMAT,
    hour12: false,
  }; // fallback

  const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(date);
  const month = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(
    date
  );
  const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(date);

  const formattedDate = applyDateFormat(config.dateFormat, day, month, year);

  const timeFormatter = new Intl.DateTimeFormat("en", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: config.hour12,
  });

  return {
    date: formattedDate,
    time: timeFormatter.format(date),
  };
}
